import {useMutation, useQueryClient} from "@tanstack/react-query";
import {ChangeEvent, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useModal} from "../../../../hooks";
import {createPostApi, deletePostApi, updatePostApi} from "../../../../api/support/board";
import {useReadPostDetail} from "./useReadPostDetail";
import {CreatePostApiProps, UpdatePostApiProps} from "../../../../types/support/board";
import moment from "moment";
import {useUser} from "../../../../util/userContext/UserContext";
import useFileHandler from "../../../../hooks/useFileHandler";

export const DefaultInputData = {
    postId: '',
    title: '',
    userId: 0,
    userName: '',
    regAt: moment().format('YYYY-MM-DD HH:mm'),
    loginId: '',
    phoneNo: '',
    body: '',
    modAt: moment().format('YYYY-MM-DD HH:mm'),
    deleteFlag: 'N',
    replyList: [],
    fileKeyList: [], // 저장/수정할 때 사용
    fileList: [], // 저장/수정할 때 사용
    fileVOList: [], // 조회결과로 사용
};

/**
 * 게시판 CRUD hook
 * @returns
 */
export const useEditPost = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {user} = useUser();
    const {handleModal, resetModal} = useModal();
    const {id} = useParams();
    const {data} = useReadPostDetail({id: id || ""});
    const [inputData, setInputData] = useState<any>({
            ...DefaultInputData
        }
    );
    const {removeFile, UploadUI, handleButtonClick } = useFileHandler({setter: setInputData});

    const createMutation = useMutation({
        mutationFn: (data: CreatePostApiProps) => createPostApi(data),
        onSuccess: (res, variables) => {
            if (res.code === "0000") {
                handleModal({
                    isOpen: true,
                    contents: "저장되었습니다.",
                    type: "alert",
                    closeAction: () => {
                        queryClient.invalidateQueries({
                            queryKey: ["readPostApi"],
                        });
                        navigate(`${process.env.PUBLIC_URL}/support/board/detail/${res.result.postId}`, {replace: true});
                    }
                })
            } else {
                handleModal({
                    isOpen: true,
                    contents: res.msg,
                    type: "alert"
                })
            }
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const updateMutation = useMutation({
        mutationFn: (data: UpdatePostApiProps) => updatePostApi(data),
        onSuccess: (res, variables) => {
            if (res.code === "0000") {
                handleModal({
                    isOpen: true,
                    contents: "저장되었습니다.",
                    type: "alert",
                    closeAction: () => {
                        queryClient.invalidateQueries({
                            queryKey: ["readPostApi"],
                        });
                        queryClient.invalidateQueries({
                            queryKey: ["readPostDetailApi", id],
                        });
                        navigate(`${process.env.PUBLIC_URL}/support/board/detail/${variables.postId}`, {replace: true});
                    }
                })
            } else {
                handleModal({
                    isOpen: true,
                    contents: res.msg,
                    type: "alert"
                })
            }
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (data: string[]) => deletePostApi(data),
        onSuccess: (res) => {
            if (res.code === "0000") {
                handleModal({
                    isOpen: true,
                    contents: "삭제되었습니다.",
                    type: "alert",
                    closeAction: () => {
                        queryClient.invalidateQueries({
                            queryKey: ["readPostApi"],
                        });
                        queryClient.invalidateQueries({
                            queryKey: ["readPostDetailApi", id],
                        });
                    }
                })
            } else {
                handleModal({
                    isOpen: true,
                    contents: res.msg,
                    type: "alert"
                })
            }
        },
        onError: (error: any) => {
            console.log("update error", error);
            handleModal({
                isOpen: true,
                contents: error.response.data.msg,
                type: "alert"
            })
        },
    });

    const deletePost = (data: string[]) => {
        if ((data.length | 0) > 0) {
            handleModal({
                isOpen: true,
                type: 'confirm',
                contents: "선택한 게시글을 삭제하시겠습니까?",
                title: "알림",
                callback: () => {
                    deleteMutation.mutate(data);
                }
            });
        }
    };

    const deletePostAndBackToSearch = (data: string[]) => {
        if ((data.length | 0) > 0) {
            handleModal({
                isOpen: true,
                type: 'confirm',
                contents: "게시글을 삭제하시겠습니까?",
                title: "알림",
                callback: async () => {
                    await deleteMutation.mutate(data);
                    navigate(`${process.env.PUBLIC_URL}/support/board/list`);
                }
            });
        }
    }

    const savePost = (data: UpdatePostApiProps) => {
        if (!data.postId) createMutation.mutate({...data});
        else updateMutation.mutate({...data, postId: id || ""});
    };

    const onChangeInputData = (
        e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const {name, value} = e.target;
        setInputData({...inputData, [name]: value});
    };

    useEffect(() => {
        if (id)
            setInputData(data);
    }, [data]);

    return {
        deletePost,
        deletePostAndBackToSearch,
        inputData,
        onChangeInputData,
        setInputData,
        UploadUI,
        handleButtonClick,
        savePost,
        removeFile
    };
};
